import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type NavLink = {
  name: string;
  type: 'route' | 'section' | 'external';
  to: string;
};
const navLinks: NavLink[] = [{
  name: 'Inicio',
  type: 'section',
  to: 'inicio'
}, {
  name: 'Mentoría (3 meses)',
  type: 'route',
  to: '/mentoria'
}, {
  name: 'Productos',
  type: 'route',
  to: '/productos'
}, {
  name: 'Ebook',
  type: 'external',
  to: 'https://protocolosniperotc.com/products/protocolosniper?variant=43460889903182'
}, {
  name: 'Testimonios',
  type: 'section',
  to: 'testimonios'
}, {
  name: 'FAQ/Soporte',
  type: 'section',
  to: 'faq'
}];
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else if (sectionId === 'inicio') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);
  const handleClick = useCallback((e: React.MouseEvent, link: NavLink) => {
    e.preventDefault();
    setIsOpen(false);
    if (link.type === 'external') {
      window.open(link.to, '_blank', 'noopener,noreferrer');
      return;
    }
    if (link.type === 'route') {
      navigate(link.to);
      return;
    }
    // type === 'section': scroll to section on home page
    const isHome = location.pathname === '/' || location.pathname === '/index';
    if (isHome) {
      scrollToSection(link.to);
    } else {
      navigate('/');
      // Wait for home page to render, then scroll
      setTimeout(() => {
        scrollToSection(link.to);
      }, 100);
    }
  }, [navigate, location.pathname, scrollToSection]);
  return <nav className={`fixed top-0 w-full z-50 transition-all duration-300 border-b h-[64px] md:h-[72px] flex items-center ${scrolled ? 'bg-neutral-950/90 backdrop-blur-lg border-red-600/20' : 'bg-transparent border-transparent'}`} data-id="element-248">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center w-full" data-id="element-249">
        <a href="#/" onClick={e => handleClick(e, {
        name: 'Inicio',
        type: 'section',
        to: 'inicio'
      })} className="text-white font-black tracking-[0.1em] sm:tracking-[0.2em] text-base sm:text-lg md:text-xl uppercase flex items-center gap-2 shrink-0" data-id="element-250">
          <span className="w-2 h-2 bg-red-600 rounded-sm" data-id="element-251"></span>
          PROTOCOLO SNIPER
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8" data-id="element-252">
          {navLinks.map(link => <a key={link.name} href={link.type === 'external' ? link.to : '#'} target={link.type === 'external' ? '_blank' : undefined} rel={link.type === 'external' ? 'noopener noreferrer' : undefined} onClick={e => handleClick(e, link)} className="text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-id="element-253">
              {link.name}
            </a>)}
        </div>

        {/* Mobile Toggle */}
        <button className="lg:hidden text-neutral-400 hover:text-white" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" data-id="element-254">
          {isOpen ? <X size={24} data-id="element-255" /> : <Menu size={24} data-id="element-256" />}
        </button>
      </div>

      {/* Thin red accent line at bottom */}
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-red-600/50 to-transparent opacity-50" data-id="element-257"></div>

      {/* Mobile Nav Panel */}
      <AnimatePresence data-id="element-258">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} className="absolute top-full left-0 w-full bg-neutral-950 border-b border-neutral-800 lg:hidden shadow-2xl" data-id="element-259">
            <div className="flex flex-col px-6 py-4" data-id="element-260">
              {navLinks.map(link => <a key={link.name} href={link.type === 'external' ? link.to : '#'} target={link.type === 'external' ? '_blank' : undefined} rel={link.type === 'external' ? 'noopener noreferrer' : undefined} onClick={e => handleClick(e, link)} className="py-4 text-sm font-medium text-neutral-300 hover:text-white border-b border-neutral-900 last:border-none" data-id="element-261">
                  {link.name}
                </a>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}